import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { endOfDay, startOfDay } from 'date-fns';
import { normalize } from 'normalizr';
import { httpRequest } from '~/adapters/httpRequest';
import * as log from '~/adapters/log';
import { graphqlRequest } from '~/adapters/graphqlRequest';
import * as transformers from '~/behaviors/transformers';
import { FetchQuestionResultsDocument, QuestionScaleRatingResponsesDocument, TimeframeInput } from '~/gql/graphql';
import * as schemas from '../schemas';
import { host } from '~/config/settings/worktango.settings';
import { IndividualResults, QuestionResponse, SurveyQuestionResult } from '~/interfaces/survey.interface';

const resetReporting = createAction('reporting/resetReporting');

const resetReportingSelections = createAction('reporting/resetReportingSelections');

const resetCompared = createAction('reporting/resetCompared');

const setReportingSurveys = createAction('reporting/setReportingSurveys', ({ surveyIds = [] }) => {
  log.debug('selectedSurveys: ', surveyIds);

  return {
    payload: {
      surveys: surveyIds,
    },
  };
});

/**
 * @example input attributeValues: {
 *   111: ['one', 'two', 'three']
 *   222: ['something', 'else']
 * }
 */
const setReportingAttributeValues = createAction(
  'reporting/setReportingAttributeValues',
  ({ attributeValues = {} }) => {
    log.debug('selectedAttributeValues: ', attributeValues);

    return {
      payload: {
        attributeValues,
      },
    };
  },
);

const removeReportingAttributeValues = createAction('reporting/removeReportingAttributes', ({ attributeIds = [] }) => {
  log.debug('removed attributeIds: ', attributeIds);
  // TODO: do some validation of the input to make sure it's in the right shape
  return {
    payload: {
      attributeIds,
    },
  };
});

// Set reporting hierarchy toggle to true or false.
const setReportingHierarchy = createAction('reporting/setReportingHierarchy', (isHierarchy) => {
  log.debug('isHierarchy: ', isHierarchy);

  return {
    payload: {
      isHierarchy: Boolean(isHierarchy),
    },
  };
});

const setComparedSurvey = createAction('reporting/setComparedSurvey', ({ surveyId = null }) => {
  log.debug('setComparedSurvey: ', surveyId);

  return {
    payload: {
      surveyId,
    },
  };
});

// Expecting input values that look like:
// attributes: {
//   111: {
//     attributeId: 111,
//     values: ['one', 'two', 'three']
//   },
//   222: {
//     attributeId: 222,
//     values: ['something', 'else']
//   }
// }
const setComparedAttributes = createAction('reporting/setComparedAttributes', ({ attributes = {} }) => {
  log.debug('comparedAttributes: ', attributes);

  return {
    payload: {
      attributes,
    },
  };
});

// Set org-wide toggle to true or false.
const setComparedOrgWide = createAction('reporting/setComparedOrgWide', (isComparedOrgWide) => {
  log.debug('isComparedOrgWide: ', isComparedOrgWide);

  return {
    payload: {
      isComparedOrgWide: Boolean(isComparedOrgWide),
    },
  };
});

// This have not been used yet, maybe for future use
const fetchQuestionResults = createAsyncThunk<
  {
    legacyResults: SurveyQuestionResult[];
    [id: number]: QuestionResponse;
  },
  {
    companyId: number;
    languageId: number;
    surveyIds: number[];
    attributes: { [id: number]: string[] };
    isHierarchyReport: boolean;
    managerEmployeeIdAttrId: number;
    selectedTimeframe?: TimeframeInput;
  }
>(
  'survey/fetchQuestionResults',
  async (
    { companyId, languageId, surveyIds, attributes, isHierarchyReport, managerEmployeeIdAttrId, selectedTimeframe },
    { rejectWithValue, dispatch },
  ) => {
    const { selectedAttributes, selectedManagers } = transformers.attributeFilterFrom(
      attributes,
      managerEmployeeIdAttrId,
    );

    const res = await graphqlRequest<any>(FetchQuestionResultsDocument, {
      surveyIds,
      languageId,
      companyIds: [companyId],
      attributes: selectedAttributes,
      managerReportIds: selectedManagers,
      isHierarchyReport,
      selectedTimeframe,
    });

    if (res.error) return rejectWithValue(res.error);

    const questionResponses = transformers.questionResponsesFrom(res.data?.overallSurveyResults);
    const normalizedData = normalize(questionResponses, [schemas.questionResponse]);

    dispatch(
      fetchQuestionAttributeResponses({
        surveyIds,
        languageId,
        companyId,
        attributes,
        isHierarchyReport,
        managerEmployeeIdAttrId,
      }),
    );

    return {
      ...normalizedData,
      legacyResults: res.data?.overallSurveyResults,
    };
  },
);

// This have not been used yet, leave for future to decide keep or not
// note: this function should only be called by fetchQuestionResults for now to make sure question responses not being override
const fetchQuestionAttributeResponses = createAsyncThunk<
  {
    legacyResults: any;
    [id: number]: any;
  },
  {
    companyId: number;
    languageId: number;
    surveyIds: number[];
    attributes: { [id: number]: string[] };
    isHierarchyReport: boolean;
    managerEmployeeIdAttrId: number;
  }
>(
  'survey/fetchQuestionRatingResponses',
  async (
    { companyId, languageId, surveyIds, attributes, isHierarchyReport, managerEmployeeIdAttrId },
    { rejectWithValue },
  ) => {
    const { selectedAttributes, selectedManagers } = transformers.attributeFilterFrom(
      attributes,
      managerEmployeeIdAttrId,
    );

    const res = await graphqlRequest<any>(QuestionScaleRatingResponsesDocument, {
      surveyIds,
      languageId,
      companyIds: [companyId],
      attributes: selectedAttributes,
      isHierarchyReport,
      managerReportIds: selectedManagers,
      requestManagerHierarchyData: true,
      includeYesNoQuestion: true,
    });

    if (res.error) return rejectWithValue(res.error);

    const { questionScaleRatingResponses } = res.data ?? {};
    const factorGroups = [...questionScaleRatingResponses.factors, ...questionScaleRatingResponses.keyFactors];

    const questionResponses = factorGroups.reduce((questions, factor) => {
      return [...questions, ...factor.questions];
    }, []);

    const responses = transformers.questionAttributeResponsesFrom(questionResponses);
    const normalizedData = normalize(responses, [schemas.questionResponse]);

    return {
      ...normalizedData,
      legacyResults: questionScaleRatingResponses, // DEPRECATED: These are just used to sync legacy context to keep legacy export works
    };
  },
);

const fetchFullResponses = createAsyncThunk<
  IndividualResults,
  {
    surveyIds: number[];
    attributes: { [id: number]: string[] };
    isHierarchyReport: boolean;
    languageId: number;
    selectedTimeframe: TimeframeInput | null;
    selectedParticipantId?: number;
  }
>(
  'survey/fetchFullResponses',
  async (
    { surveyIds, attributes, isHierarchyReport, languageId, selectedTimeframe, selectedParticipantId },
    { rejectWithValue },
  ) => {
    const selectedAttributes: { id: number; values: string[] }[] = [];
    if (Object.keys(attributes).length > 0) {
      Object.keys(attributes).forEach((key) => {
        selectedAttributes.push({
          id: Number(key),
          values: attributes[key],
        });
      });
    }
    const res = await httpRequest<any>(`${host}/api/reporting/v1/individual-result-report`, {
      method: 'POST',
      body: {
        surveyIds,
        selectedAttributes,
        isHierarchyReport,
        languageId,
        selectedTimeframe,
        selectedParticipantId,
      },
    });

    if (res.error) return rejectWithValue(res.error);

    // refactor: rewrite functions in fullResponsesExport, and let this action to finish export
    return res.surveyResponsesExport;
  },
);

// `valueId`/`valueName` are always written, never omitted: the reducer merges this payload over the
// previous selection, so leaving them out would keep a stale value id alive when the scope changes.
const setCompareBenchmarks = createAction(
  'reporting/setCompareBenchmarks',
  ({ name, scope, valueId = null, valueName = null }) => {
    const result: { name?: string; scope: string; valueId: number | null; valueName: string | null } = {
      scope,
      valueId,
      valueName,
    };
    if (name) result.name = name;

    return {
      payload: result,
    };
  },
);

const hasLoadedBenchmarks = createAction('reporting/hasLoadedBenchmarks');

const setTimeSliceSearch = createAction(
  'reporting/setTimeSliceSearch',
  ({ startTime = null, endTime = null, timeframe = null }) => {
    return {
      payload: {
        startTime: startTime ? startOfDay(startTime) : null,
        endTime: endTime ? endOfDay(endTime) : null,
        timeframe,
      },
    };
  },
);

const setCompareTimeSlice = createAction(
  'reporting/setCompareTimeSlice',
  ({ startTime = null, endTime = null, timeframe = null }) => {
    return {
      payload: {
        startTime,
        endTime,
        timeframe,
      },
    };
  },
);

const setSelectedParticipant = createAction('reporting/setSelectedParticipant', ({ participant = null }) => {
  return {
    payload: {
      participant,
    },
  };
});

const resetParticipant = createAction('reporting/resetParticipant');

const setTrendsSelectedTimeframes = createAction(
  'reporting/setTrendsSelectedTimeframes',
  ({ selectedTimeframes = [] }) => {
    // Need update endTimeUTC to the end of that day, to cover end day data
    const updatedSelectTimeframes = selectedTimeframes.map((timeframe) => ({
      ...timeframe,
      endTimeUTC: endOfDay(timeframe.endTimeUTC),
    }));
    return {
      payload: {
        selectedTimeframes: updatedSelectTimeframes,
      },
    };
  },
);

export const reportingActions = {
  resetReporting,
  resetReportingSelections,
  resetCompared,
  setReportingSurveys,
  setReportingAttributeValues,
  setReportingHierarchy,
  removeReportingAttributeValues,
  setComparedSurvey,
  setComparedAttributes,
  setComparedOrgWide,
  fetchQuestionAttributeResponses,
  fetchQuestionResults,
  fetchFullResponses,
  setCompareBenchmarks,
  hasLoadedBenchmarks,
  setTimeSliceSearch,
  setCompareTimeSlice,
  setSelectedParticipant,
  resetParticipant,
  setTrendsSelectedTimeframes,
};
